import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { queryClient } from '@/lib/query-client'
import { AuthProvider, useAuth } from '@/lib/AuthContext'
import ErrorBoundary from '@/components/ErrorBoundary'
import Layout from '@/Layout'
import PageNotFound from '@/lib/PageNotFound'
import { usePushNotifications } from '@/lib/usePushNotifications'
import IncomingCallModal from '@/components/IncomingCallModal'

// Pages are code-split (review item §3.2): the landing page no longer ships
// Leaflet, Recharts, Radix, Stripe, etc. Each route loads its own chunk.
const Home = lazy(() => import('@/pages/Home'))
const AuthPage = lazy(() => import('@/pages/AuthPage'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Challenges = lazy(() => import('@/pages/Challenges'))
const Friends = lazy(() => import('@/pages/Friends'))
const Messages = lazy(() => import('@/pages/Messages'))
const Live = lazy(() => import('@/pages/Live'))
const LiveRoom = lazy(() => import('@/pages/LiveRoom'))
const Initiatives = lazy(() => import('@/pages/Initiatives'))
const Rewards = lazy(() => import('@/pages/Rewards'))
const Settings = lazy(() => import('@/pages/Settings'))
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'))
const Pricing = lazy(() => import('@/pages/Pricing'))
const Profile = lazy(() => import('@/pages/Profile'))
const Feed = lazy(() => import('@/pages/Feed'))
const Flicks = lazy(() => import('@/pages/Flicks'))
const CallRoom = lazy(() => import('@/pages/CallRoom'))

function FullScreenLoader() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#f0f4f3]">
      <div className="w-10 h-10 border-4 border-gray-200 border-t-green-500 rounded-full animate-spin" />
    </div>
  )
}

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth()

  // `loading` now has a single source of truth in AuthContext (it resolves once
  // the initial session — and, if signed in, the profile — has settled), so no
  // local timeout band-aid is needed here.
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#f0f4f3]">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-green-500 rounded-full animate-spin mx-auto" />
          <p className="text-gray-400 text-sm">Loading…</p>
        </div>
      </div>
    )
  }

  return isAuthenticated ? children : <Navigate to="/auth" replace />
}

function PushNotificationManager() {
  usePushNotifications()
  return null
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
      <Route path="/pricing" element={<Layout><Pricing /></Layout>} />

      {/* Protected */}
      <Route path="/dashboard"   element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
      <Route path="/challenges"  element={<PrivateRoute><Layout><Challenges /></Layout></PrivateRoute>} />
      <Route path="/friends"     element={<PrivateRoute><Layout><Friends /></Layout></PrivateRoute>} />
      <Route path="/messages"    element={<PrivateRoute><Layout><Messages /></Layout></PrivateRoute>} />
      <Route path="/live"        element={<PrivateRoute><Layout><Live /></Layout></PrivateRoute>} />
      <Route path="/live/:id"    element={<PrivateRoute><Layout><LiveRoom /></Layout></PrivateRoute>} />
      <Route path="/initiatives" element={<PrivateRoute><Layout><Initiatives /></Layout></PrivateRoute>} />
      <Route path="/rewards"     element={<PrivateRoute><Layout><Rewards /></Layout></PrivateRoute>} />
      <Route path="/settings"    element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
      <Route path="/profile/:userId?" element={<PrivateRoute><Layout><Profile /></Layout></PrivateRoute>} />
      <Route path="/feed"        element={<PrivateRoute><Layout><Feed /></Layout></PrivateRoute>} />
      <Route path="/flicks"      element={<PrivateRoute><Layout><Flicks /></Layout></PrivateRoute>} />
      <Route path="/call/:id"    element={<PrivateRoute><Layout><CallRoom /></Layout></PrivateRoute>} />

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
            <PushNotificationManager />
            <IncomingCallModal />
            <Suspense fallback={<FullScreenLoader />}>
              <AppRoutes />
            </Suspense>
          </Router>
          <Toaster
            richColors
            position="top-right"
            duration={2500}
            closeButton
            toastOptions={{
              style: { marginTop: '70px' },
            }}
          />
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}
